import { useState, useEffect } from 'react'
import { Box, Text, FormControl, FormLabel, VStack } from '@chakra-ui/react'
import cx from 'classnames'
import Errors from 'configs/errors'
import { useStores } from 'hooks/useStores'
import Logo from 'images/logo.png'
import { observer } from 'mobx-react'
import { useFormContext, useForm, FormProvider, SubmitHandler } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Form } from 'reactstrap'
import Button from 'components/Button'
import AuthLayout from 'components/Layouts/AuthLayout'
import Separator from 'components/Separator'
import TextField from 'components/TextField'
import SSOButton from 'components/SSOButton'
import { S3FileTypeEnum } from 'constants/aws'
import routes from 'routes'
import useSSO from 'hooks/useSSO'

import { getS3FileUrl } from 'utils/images'
import styles from './styles.module.scss'

interface ILoginForm {
  username: string
  password: string
  organizationId: number
}

type LoginFormProps = {
  onLogin?: SubmitHandler<ILoginForm>
  subdomain: string
  submitting?: boolean
  ssoOnlyMode?: boolean
  onToggleToEmailLogin?: () => void
}

const LoginForm = ({ onLogin = () => {}, subdomain, submitting = false, ssoOnlyMode = false, onToggleToEmailLogin }: LoginFormProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useFormContext<ILoginForm>()
  const { organizationStore } = useStores()
  const { organization, publicSSOConfigs } = organizationStore
  
  // Initialize SSO callback handling
  useSSO()
  
  // Check if SSO is available
  const hasSSOConfigs = organization?.id && publicSSOConfigs?.filter(config => config.enabled).length > 0
  
  // SSO-only mode UI
  if (ssoOnlyMode) {
    return (
      <VStack 
        w="min(400px, calc(100vw - 60px))" 
        p="0"
        bg="white" 
        border="none" 
        gap="24px"
        align="stretch"
      >
        <Text as="h3" textAlign="center" color="gray.700" fontSize="lg" fontWeight="600" m={0}>
          Login to HowFactory using your Organization account
        </Text>
        <SSOButton 
          backgroundColor={'#00A9EB'}
          width="min(400px, calc(100vw - 60px))"
        />
        <Text textAlign="center" fontSize="sm" m={0} color="gray.700">
          Don't have an Organization account?{' '}
          <Button 
            variant="link" 
            color="#00A9EB" 
            onClick={onToggleToEmailLogin}
            style={{ padding: 0, color: '#00A9EB', fontWeight: '600', textDecoration: 'none' }}
          >
            Login with email
          </Button>
        </Text>
      </VStack>
    )
  }
  
  // Regular email/password mode UI
  return (
    <Form onSubmit={handleSubmit(onLogin)}>
      <VStack 
        w="min(400px, calc(100vw - 60px))" 
        bg="white" 
        border="none" 
        gap="24px"
        align="stretch"
      >
        <FormControl>
          <VStack gap="8px" align="flex-start">
          <FormLabel fontSize="md" fontWeight="500" color="gray.700" p={0} m={0}>
            Your username/email
          </FormLabel>
          <TextField
            autoComplete="username"
            placeholder="Enter your username/email"
            {...register('username', {
              required: {
                value: true,
                message: Errors.USERNAME_REQUIRED
              }
            })}
            onChange={(e: { currentTarget: { value: string } }) => setValue('username', e.currentTarget.value)}
            error={!!errors.username}
          />
          </VStack>
        </FormControl>
        <FormControl>
        <VStack gap="8px" align="flex-start">
          <FormLabel fontSize="md" fontWeight="500" color="gray.700" p={0} m={0}>
            Password
          </FormLabel>
          <TextField
            placeholder="◌◌◌◌◌◌◌◌◌◌"
            type="password"
            autoComplete="current-password"
            {...register('password', {
              required: {
                value: true,
                message: Errors.PASSWORD_REQUIRED
              }
            })}
            onChange={(e: { currentTarget: { value: string } }) => setValue('password', e.currentTarget.value)}
            error={!!errors.password}
          />
            <Text textAlign="start" fontWeight="bold">  
            <Link 
              to={routes.forgotPassword.value} 
              style={{ 
                fontSize: '14px',
                color: '#00A9EB',
                textDecoration: 'none'
              }}
            >
              Forget your password?
            </Link>
            </Text>
        </VStack>
        </FormControl>
        <VStack gap="23px" align="flex-start" w="100%">
          <Button 
            type="submit"
            disabled={submitting} 
            style={{ padding: '0px', margin: '0px' }} 
            size="lg" 
            className={styles.submitBtn} 
            color="secondary" 
            onClick={handleSubmit(onLogin)}
          >
            Sign in{submitting ? '...' : ''}
          </Button>
          {hasSSOConfigs && (
            <>
              <Separator text="or" className={cx(styles.centerItem, styles.greySep)} style={{ width: '100%' }} />
              <SSOButton backgroundColor={'#2A3E60'} width="min(400px, calc(100vw - 60px))" />
            </>
          )}
        </VStack>
      </VStack>
    </Form>
  )
}

const LoginPage = () => {
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)
  const [showEmailLogin, setShowEmailLogin] = useState(false)
  const { authStore, organizationStore } = useStores()
  const formMethods = useForm<ILoginForm>()

  const [subdomain] = window.location.host.replace(/^(www\.)/, '').split('.')
  
  // Load organization data and check SSO mode
  useEffect(() => {
    if (subdomain) {
      organizationStore.getOrganizationBySubdomain(subdomain)
    }
  }, [subdomain])

  const handleLogin = (data: ILoginForm) => {
    const { username, password } = data
    if (username && password) {
      setSubmitting(true)
      authStore
        .login(username, password)
        .then(loggedin => {
          if (authStore.resetPasswordToken && !loggedin) {
            toast.warning('Your account has been reset password. Please enter new password')
            navigate(`${routes.resetPassword.value}?token=${authStore.resetPasswordToken}`)
          }
          if (authStore.isDisabled && !loggedin) {
            toast.error('Your account currently has been deactivated. Please contact the administrator.')
            setSubmitting(false)
          }
          if (loggedin) {
            navigate(routes.dashboard.value)
          }
        })
        .catch((err: any) => {
          const message = err?.message || 'Login failed. Please try again.'
          toast.error(message)
          setSubmitting(false)
        })
    }
  }

  // Determine if we should show SSO-only mode
  const shouldShowSsoOnly = organizationStore.ssoOnlyAuth && !showEmailLogin

  return (
    <FormProvider {...formMethods}>
      <AuthLayout>
        <LoginForm 
          subdomain={subdomain} 
          onLogin={handleLogin} 
          submitting={submitting}
          ssoOnlyMode={shouldShowSsoOnly}
          onToggleToEmailLogin={() => setShowEmailLogin(true)}
        />
      </AuthLayout>
    </FormProvider>
  )
}

export default observer(LoginPage)
